/* =========================================================================
 * ONE CHAMPIONSHIP, MANY CHAMPIONS.
 *
 * Brian: "Thats like going into ncaa championships for wrestling. There isn't
 * one wrestling champion, there is a champion for every weight class, then
 * there are also team championships, same goes for track and field
 * championships and swimming, and any individual sport."
 *
 * ⚠️ WHY THIS IS THE MOST EXPENSIVE ASSUMPTION IN THE BOARD.
 *
 * Every record holds ONE `current_champion`. For a team sport that is correct.
 * For an individual sport it is wrong in the direction that costs the most: an
 * NCAA wrestling championship crowns TEN weight-class champions and a team
 * champion, and the board has room for one. So either
 *
 *   · the hunt writes one of them and the record silently claims that is THE
 *     champion — nine wrong, one right, and no way to tell which; or
 *   · it writes none, and a championship with eleven ring-buying winners sits
 *     on the board reading "no champion yet" forever.
 *
 * Both have been happening. Every weight class is a ring. Every event at a
 * track championship is a ring. The team title is a ring on top. Treating the
 * parent as a single crown is not a display bug, it is most of the product.
 *
 * ⚠️ WHAT THIS MODULE DOES AND DOES NOT DO.
 *
 * It CLASSIFIES. It says "this championship crowns many, along this axis, and
 * probably also a team title", so the hunt knows to look for N answers instead
 * of one and the board can render a container rather than a gap. It creates
 * nothing and writes nothing — `splitPlan.js` and `/api/champion-resolve`
 * remain the only things that make records, and a human still presses the
 * button.
 *
 * ⚠️ IT NEVER INVENTS A COUNT. Where the number is a fixed, checkable fact
 * (NCAA wrestling is ten weight classes) it is stated. Where it varies by meet
 * — track events, swimming events, age divisions — `count` is null and the
 * caller must say "several", not a made-up figure. A confident wrong number
 * here would be read as the number of rings to quote.
 *
 * PURE, no imports — verify-multichampion.mjs drives it directly under node.
 * ========================================================================= */

const S = (v) => (v == null ? '' : String(v)).trim()
const L = (v) => S(v).toLowerCase()

/* ---- THE AXES ----------------------------------------------------------
 * How a sport divides its winners. `label` is what a rep calls one child;
 * `plural` names the set; `count` is only set where it is a stable fact.
 * ---------------------------------------------------------------------- */
export const AXES = {
  weight: { key: 'weight', label: 'weight class', plural: 'weight classes' },
  event: { key: 'event', label: 'event', plural: 'events' },
  age: { key: 'age', label: 'age division', plural: 'age divisions' },
  apparatus: { key: 'apparatus', label: 'apparatus', plural: 'apparatus' },
  vehicleClass: { key: 'vehicleClass', label: 'class', plural: 'classes' },
  weapon: { key: 'weapon', label: 'weapon', plural: 'weapons' },
  boat: { key: 'boat', label: 'boat class', plural: 'boat classes' },
  division: { key: 'division', label: 'division', plural: 'divisions' },
}

/* ---- THE SPORTS --------------------------------------------------------
 * Individual sports crown per-competitor. `team` marks the ones that ALSO
 * award a team title from the same meet — the ring a school actually buys in
 * volume, and the one most likely to be missed because the individual results
 * dominate the coverage.
 * ---------------------------------------------------------------------- */
const SPORTS = [
  { match: /wrestl/, axis: 'weight', team: true },
  { match: /track|cross country|athletics/, axis: 'event', team: true },
  { match: /swim|dive|diving/, axis: 'event', team: true },
  { match: /gymnast/, axis: 'apparatus', team: true },
  { match: /fenc/, axis: 'weapon', team: true },
  { match: /golf/, axis: 'division', team: true },
  { match: /tennis/, axis: 'division', team: true },
  { match: /row|crew/, axis: 'boat', team: true },
  { match: /ski|snowboard/, axis: 'event', team: false },
  { match: /skat/, axis: 'division', team: false },
  { match: /equestrian|dressage|vault|eventing/, axis: 'division', team: true },
  { match: /shoot|archery|clay target/, axis: 'event', team: true },
  { match: /powerlift|weightlift/, axis: 'weight', team: true },
  { match: /judo|karate|taekwondo|jiu-jitsu|jiu jitsu|boxing/, axis: 'weight', team: false },
  { match: /motorsport|racing|moto|nhra|indycar|drag/, axis: 'vehicleClass', team: false },
  { match: /sail/, axis: 'boat', team: false },
  { match: /rodeo/, axis: 'event', team: false },
  { match: /bowling/, axis: 'division', team: true },
  { match: /cycling|triathlon/, axis: 'event', team: false },
  { match: /esports/, axis: 'division', team: false },
]

/* Youth baseball/softball bodies run one World Series PER AGE. This is an
 * organiser pattern rather than a sport property, so it is matched on the
 * record NAME, not the sport.
 *
 * ⚠️ LITTLE LEAGUE IS DELIBERATELY ABSENT, on evidence. SR already holds its
 * tiers as SEPARATE records — "Junior League Baseball World Series", "Senior
 * League…", "Intermediate…" are their own rows beside "Little League Baseball
 * World Series". Flagging the flagship as multi-champion would tell a rep the
 * Pariba Little League crown is incomplete when it is exactly right, which is
 * the same class of false claim this whole module exists to stop. */
const AGE_GRADED = /cal ripken|babe ruth|dixie youth|pony league|aau|usssa/

/* ⚠️ A NAME THAT ALREADY STATES ITS AGE IS A CHILD, NOT A CONTAINER.
 * "Cal Ripken 10U World Series", "Junior League…", "Ages 8-9…" each crown one
 * winner. Only the unqualified parent divides. */
const AGE_QUALIFIED = /\b(\d{1,2}\s*u|u\s*\d{1,2}|ages?\s*\d|junior|senior|intermediate|rookie|major|minor)\b/

/* A count only where it is a fixed, checkable fact. */
const KNOWN_COUNTS = [
  { match: /ncaa/, sport: /wrestl/, count: 10, note: 'NCAA wrestling has ten weight classes' },
]

/**
 * multiChampion(record) → null | {
 *   axis, label, plural, team, count, reason, expected
 * }
 *
 * `null` means one champion is the right answer — a team sport, or a record we
 * have no reason to think divides. Returning null rather than a guess keeps
 * football, basketball and every school title exactly as they are.
 */
export function multiChampion(c) {
  const sport = L(c?.sport)
  const name = L(c?.name)
  if (!sport && !name) return null

  /* An already-split CHILD crowns one winner — it IS the division. Detected by
   * the parent link the splitter writes, never by guessing from the name. */
  if (S(c?.parentId) || L(c?.championSearchTag) === 'split_child') return null

  const hit = SPORTS.find((s) => s.match.test(sport))
  const ageGraded = AGE_GRADED.test(name) && !AGE_QUALIFIED.test(name)
  if (!hit && !ageGraded) return null

  const axisKey = ageGraded && !hit ? 'age' : hit.axis
  const axis = AXES[axisKey] || AXES.division
  const team = ageGraded && !hit ? false : hit.team

  const known = KNOWN_COUNTS.find((k) => k.match.test(name) && k.sport.test(sport))

  return {
    axis: axis.key,
    label: axis.label,
    plural: axis.plural,
    team,
    /* ⚠️ null is the honest default. See the header — a made-up number here
     * gets read as how many rings to quote. */
    count: known ? known.count : null,
    reason: known
      ? known.note
      : ageGraded && !hit
        ? 'This body runs a separate World Series for each age group'
        : `${axis.plural === 'apparatus' ? 'Apparatus' : axis.plural[0].toUpperCase() + axis.plural.slice(1)} are crowned separately in this sport`,
    expected: known ? `${known.count} ${axis.plural}` : `several ${axis.plural}`,
  }
}

/**
 * The sentence a rep reads on a parent record.
 *
 * 🔴 REWRITTEN 2026-10-19 — IT NO LONGER DEMANDS A SPLIT. Brian Kass, that
 * date: "Individual championships are NOT split. The champion of record is the
 * SCHOOL, and the individual athlete and event are attached as the reference."
 * · "Retire the red 'one name can't be all of them' flag and the
 * outcome:'split' path in the hunt agent and multiChampion, so nothing keeps
 * demanding splits."
 *
 * ⚠️ THE OLD SENTENCE ENDED: "Recording a single name here would claim one of
 * them is THE champion and hide the rest." That was true when one record held
 * one name. It is false now — one record holds a school plus N athlete
 * references — and left in place it would have told every rep that a correctly
 * crowned record was incomplete, and pushed them toward a split the write path
 * now refuses. A prompt for work the contract rejects is worse than silence.
 *
 * ⚠️ WHAT IS DELIBERATELY KEPT: the classification and the COUNT. "NCAA
 * wrestling has ten weight classes" is still the single most useful thing on
 * the row, because it says how many athlete references this crown should end
 * up carrying and how many rings that is. The module was always right that
 * the multiplicity exists; the ruling only changes where it is recorded. So
 * the note now sets the EXPECTATION against the references, and the team-title
 * reminder stays because that is a separate record and a separate order.
 */
export function multiChampionNote(c) {
  const m = multiChampion(c)
  if (!m) return ''
  const teamPart = m.team
    ? ' There is normally a TEAM title from the same meet as well — a separate record and usually the biggest single order, and the one most often missed because the individual results dominate the coverage.'
    : ''
  return `This crowns ${m.expected}. ${m.reason}. The school is the champion of record and each `
    + `${m.label} winner attaches as an athlete reference on this one record — it is not split into `
    + `children.${teamPart}`
}

/**
 * How many athlete references this crown is still short of, where the count is
 * a checkable fact. `null` when the number varies by meet — never a guess,
 * because a confident wrong number here reads as how many rings to quote.
 *
 * Replaces `overclaims()`, which asked "does this record carry one name where
 * many belong?" — a question whose only correct answer was "split it", and
 * which is why it had to go rather than be rewired.
 */
export function referencesExpected(c) {
  const m = multiChampion(c)
  if (!m) return null
  return { count: m.count, label: m.label, plural: m.plural, team: m.team, expected: m.expected }
}
